import React from "react";
import { Link } from "react-router-dom";

import { Rocket, Calculator } from "lucide-react";
import CTAButtonMob from "../CTA/CTAButtonMob.jsx";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";

import Tooltip from "./Tooltip";
import "./guides.css";

import guidesConfig from "../../config/guidesConfig.js";

import mbl from './images/mb-lsb.png'
import ms from './images/mb-sm.png'

const HorseRefundGuide = () => {
  const meta = guidesConfig.horseRefundGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.horseRefundGuide?.route || "/guides"}
      />

        <p>Horse racing refund offers, often called <span className="key">money back offers</span>, are a fantastic way to boost your matched betting profits.</p>
        
        <p>
            We'll explain exactly how money back offers work, how to take advantage, along with handy tips so you can confidently profit from these bookmaker promotions.
            </p>

      <section>
        <h2>What Are Horse Racing Refund Offers?</h2>
      <h6 className="info">
        Refund offers return your stake as cash or a free bet
        when specific conditions apply.
      </h6>
      <p>For example, you might see:</p>
        <ul>
          <li>Money Back if your horse finishes <strong>2nd</strong></li>
          <li>Money Back if your horse finishes <strong>2nd, 3rd, or 4th</strong></li>
          <li>Refund if your horse <strong>loses to the SP favourite</strong></li>
          <li>Refund if your horse <strong>fails to finish the race</strong></li>
        </ul>
        <p>Refund amounts typically range from £5 to £20, paid as cash or a free bet.</p>
        <p>Cash refunds are generally better than free bets, since we can <Link to="/guides/free-bet-value-guide">only lock in 70-80% of the value of a free bet</Link>.</p>
        <p>Not all refund / money back offers are for horses! There will also be variations of this for golf and tennis, for example, which can be approached in a similar way.</p>
      </section>

      <section>
        <h2>How Do Horse Racing Refund Offers Work?</h2>
        <h6 className="info">To profit, we accept a small qualifying loss, and hope that the refund is triggered.</h6>
        <ol>
            <p>Here's how it works, in a nutshell:</p>
          <li>
            Place a <span className="back">back</span> bet on a horse at the bookmaker.
          </li>
          <li>
            <span className="lay">Lay</span> the same horse on a{" "}
            <Tooltip content="An online platform where you bet against other users rather than the bookmaker.">
              betting exchange
            </Tooltip>{" "}
            to offset your risk.
          </li>
          <li>
            Receive your <span className="key">refund</span> if the offer condition happens
            (e.g., horse finishing 2nd).
          </li>
        </ol>

        <div className="example-container">
            
            <h4>Example</h4>
            <p>
            <strong>LiveScoreBet</strong> is offering money back as a free bet up to £10 if our
            horse finishes 2nd, 3rd or 4th at the 13:40 Newcastle.
            </p>
            <p>We check the odds, and compare them with the odds at our chosen exchange, Smarkets:</p>
            <div className="grid-fixed g2">
                <div className="col">
                    <h3>LiveScoreBet</h3>
                    <img src={mbl} alt="LiveScoreBet Newcastle horse racing offer" />
                </div>
                <div className="col">
                <h3>Smarkets</h3>

                <img src={ms} alt="Smarkets Newcastle horse racing exchange" />
                </div>
            </div>
            <p>We see that <span className="key">So Darn Hot</span> has close odds. The <span className="back">back</span> odds are <strong>4.0</strong>, while the <span className="lay">lay</span> odds are <strong>4.1</strong>.</p>
            <p>Using our <Link to="/calculators/lay-stake-calculator">matched betting calculator</Link>, we work out that we could lay <strong>£9.76</strong> at odds of 4.1 and our <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip> will be <strong>£30.26</strong>.
            Our qualifying loss will be <span className="key">26p</span>.</p>
            <ul>
            <li>We place a £10 <span className="back">back</span> bet on <span className="key">So Darn Hot</span> at LiveScoreBet.</li>
            <li>We place a £9.76 <span className="lay">lay</span> bet on <span className="key">So Darn Hot</span> at Smarkets.</li>
            </ul>
            <p><strong>Possible outcomes:</strong></p>
            <ul>
            <li>
                <strong><span className="key">So Darn Hot</span> wins:</strong> We win £30 at LiveScoreBet and lose £30.26 at Smarkets, with an <strong>overall loss of 26p</strong>.
            </li>
            <li>
                <strong><span className="key">So Darn Hot</span> finishes 2nd, 3rd or 4th:</strong> We lose our £10 stake at LiveScoreBet, win £9.76 at Smarkets, and get <span className="key">£10 cash refund</span>, earning £9.74 overall profit.
            </li>
            <li>
                <strong><span className="key">So Darn Hot</span> finishes outside top 3:</strong> We lose our £10 stake at LiveScoreBet, and win £9.76 at Smarkets, with an <strong>overall loss of 26p</strong>.
            </li>
            </ul>
        </div>
      </section>

      {/* <CTALarge
        to="/calculator/refund-offers"
        text="Go to Refund Offers Calculator"
        icon={Calculator}
      /> */}

      <section>

    <h2>What's the Right Level of Risk to Take?</h2>
    <h6 className="info">The easier it is to trigger the offer, the more risk we would be willing to take.</h6>
    <p>In general, it’s smart to keep your <span className="key">qualifying losses</span> (QLs) low — ideally under £1.</p>
    <p>For example, you might take more risk if some of these apply:</p>
      <ul>
        <li>The trigger condition is more likely (e.g. money back if 2nd or 3rd is better than money back if 2nd)</li>
        <li>There are fewer runners in the race</li>
        <li>Your horse's odds make it more likely to trigger the condition (e.g. odds of around 2-5 for money back if 2nd or 3rd)</li>
        <li>Refunds are in cash rather than free bets (you can <Link to="/guides/free-bet-value-guide">only lock in 70-80% of the value of a free bet</Link> as profit)</li>
      </ul>
    <h6 className="warn">While many of these offers are profitable, others have very little value and are only worth doing if you can achieve zero, or close to zero, qualifying loss. Even then, they may not be worth the effort.</h6>
      <p><strong>Use our <Link to="/calculators/refun">Refund Offer Calculator</Link> to assess whether your offer and horse is profitable.</strong></p>

    <CTALarge 
        to="/calculators/refund" 
        text="Go to the Refund Offer Calculator" 
        icon={Calculator} 
      />

        <h2>Tips to Maximise Profits</h2>

        <h6 className="info">
          Aim for qualifying losses under £1 by matching odds as closely as possible.
        </h6>

        <h3>Use OddsMonkey for Minimising Qualifying Losses</h3>
        <p>
        <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> shows which horses qualify for offers, finds the tightest matches across bookies and exchanges, and even lets you place bets with one click. It’s a paid tool, but if you’re doing refund offers regularly — especially when races are about to go off — it can save loads of time and prevent costly mistakes.
        </p>
        <p>Matched Edge readers can get a <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">free trial</a>. We're huge fans of OddsMonkey (and all use it), and recommend it because it's really an indispensible tool for matched bettors.</p>

        <h3>Check Terms Carefully</h3>
        <ul>
          <li>What's the <strong>maximum refund</strong>?</li>
          <li>
            What are the <strong>minimum odds</strong>?
          </li>
          <li>Will you receive <strong>cash or free bets</strong>?</li>
          <li>
            What are the <strong>eligible races</strong>?
          </li>
          <li>
            Do you need to <strong>opt in</strong>? You might need to opt in, collect a token or apply the promo in your betslip.
          </li>
        </ul>

        <h3>Select Horses Wisely</h3>
        <p>
          Pick horses with odds more likely to
          trigger refund conditions. This will depend on the race and offer terms, but might have a sweet spot between 2.0 and 5.0. Even so, horse racing is unpredictable, but this will make a small difference.
        </p>
      </section>
<h2>Can I Consistently Profit from Refund Offers?
</h2>
<p>You’ll often take small qualifying losses, but relatively often, a refund triggers and gives you a solid gain. Over time, those gains easily outweigh the losses.</p>
<p>When you keep your qualifying losses low, you minimise risk and maximise profit.</p>
    <p>With horse racing refunds, we don't expect to see huge swings and volatility (as you might with doing horse racing <Link to="/guides/extra-place-offers-guide">extra place offers</Link>).
</p>

      <section>
      <h2>Advanced Strategies</h2>
<h3>Underlaying the Favourite</h3>
<h6 className="info"><span className="key">Underlaying</span> means laying slightly less on the exchange than normal, to <em>reduce or eliminate</em> your qualifying loss if your selection wins.</h6>
<p><span className="key">Underlaying</span> could be a good opportunity in small fields with a clear favourite and decent chance your horse finishes 2nd. 
</p>
<p>
Bear in mind that favourites lose more often than we tend to expect and their true odds are often longer than the odds suggest. If they don’t win, you could miss the refund and your underlay increases your loss.
</p>

        <h3>Lock In Guaranteed Profits</h3>
        <p>
          Experienced bettors sometimes lock in profits by placing a back bet
          at the bookmaker and laying separate bets on both the win and place
          markets at the exchange. Use a matched betting refund calculator to
          find the perfect stakes and guarantee a small profit regardless of
          outcome.
        </p>
      </section>

      <section>
        <h2>Where Can I Find Horse Racing Refund Offers?</h2>
        <p>So, what are the top bookies for money back offers? At the time of writing, we suggest you check out:</p>
        <ul>
          <li><strong>Paddy Power</strong> (Often runs Money Back if 2nd, 3rd or 4th at weekends)</li>
          <li><strong>LiveScoreBet</strong> (Often runs Money Back if 2nd, 3rd or 4th at weekends)</li>
          <li><strong>Betfred</strong> (Often runs various Money Back offers throughout the week)</li>
          <li><strong>Unibet</strong> (Often runs Money Back if 2nd or 3rd at weekends)</li>
          <li><strong>Sky Bet</strong> (Occasionally offers various Money Back offers)</li>
        </ul>
        <p>Many major bookies like <strong>Bet365</strong> and <strong>William Hill</strong> will have horse racing refunds during major events.</p>
      </section>
      <CTAButtonMob
            to="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317"
            external='true'
            text="Use OddsMonkey to Find Racing Refund Offers"
            buttonText="Visit"
            icon={Rocket}
            tabText="Free trial available"
        />

      <section>
        <h2>That's it!</h2>
        <p>
          Horse racing refund offers are a low-risk way to boost matched betting
          profits. Understand the terms, keep losses minimal, and apply
          advanced strategies to maximise earnings.
        </p>
      </section>
    </div>
  );
};

export default HorseRefundGuide;
